import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ApolloLink,
  FetchResult,
  InMemoryCache,
  NextLink,
  Observable,
  Operation,
} from '@apollo/client/core';
import { RetryLink } from '@apollo/client/link/retry';
import { ObservableSubscription } from '@apollo/client/utilities/observables/Observable';
import { Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { AppInsights } from 'applicationinsights-js';
import { OperationDefinitionNode } from 'graphql';
import { uuid } from 'src/util/uuid';
import { getOrElse } from 'src/util/values';
import { configuration } from '../app.config';

export class AppInsightsDependencyTrackingLink extends ApolloLink {
  constructor(private readonly _graphQLUrl: string) {
    super();
  }

  public request(operation: Operation, forward: NextLink): Observable<FetchResult> | null {
    return new Observable((observer) => {
      let sub: ObservableSubscription | undefined;
      const begin = performance.now();
      const operationId: string | null = operation.getContext().operationId;
      const opDefs: OperationDefinitionNode[] = operation.query.definitions
        .filter((d) => d.kind === 'OperationDefinition')
        .map((n) => n as OperationDefinitionNode);
      const graphQLOperationType =
        opDefs.length > 1 ? 'multiple' : opDefs.length === 1 ? opDefs[0].operation : 'unknown';
      const operationName = 'GraphQL ' + graphQLOperationType + ': ' + operation.operationName;
      if (operationId == null) {
        throw new Error('Expected non-null operation id');
      }
      const duration = () => performance.now() - begin;
      try {
        sub = forward(operation).subscribe({
          next: (result) => {
            AppInsights.trackDependency(
              operationId,
              'GraphQL',
              this._graphQLUrl,
              operationName,
              duration(),
              true,
              -200,
            );

            observer.next(result);
          },
          error: (networkError) => {
            AppInsights.trackDependency(
              operationId,
              'GraphQL',
              this._graphQLUrl,
              operationName,
              duration(),
              false,
              -400,
            );
            observer.error(networkError);
          },
          complete: () => {
            observer.complete();
          },
        });
      } catch (err) {
        observer.error(err);
      }
      return () => {
        if (sub != null) {
          sub.unsubscribe();
        }
      };
    });
  }
}

export const setAppInsightsOperationIdLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext();
  const operationId = getOrElse(
    operation.getContext().operationId,
    operation.operationName + '-' + uuid(),
  );
  const headers = context.headers != null ? context.headers : {};
  headers['request-id'] = operationId;
  operation.setContext({ headers, operationId });
  return forward != null ? forward(operation) : null;
});

export function createClientLinks(httpLink: HttpLink) {
  const http = httpLink.create({
    uri: configuration.tcs.graphQLUri,
  });

  const retryLink = new RetryLink({
    delay: {
      initial: 500,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error, _operation) => {
        /*
        We should only retry on non-user errors. We want user errors
        to surface immediately (and not be delayed by the retry mechanism).
        */
        return error instanceof HttpErrorResponse && error.status >= 500;
      },
    },
  });

  return ApolloLink.from([
    retryLink,
    setAppInsightsOperationIdLink,
    new AppInsightsDependencyTrackingLink(configuration.tcs.graphQLUri),
    http,
  ]);
}

@Injectable({ providedIn: 'root' })
export class ApolloInitService {
  constructor(private _apollo: Apollo, private _httpLink: HttpLink) {}

  public init() {
    this._apollo.create({
      link: createClientLinks(this._httpLink),
      cache: new InMemoryCache(),
    });
  }
}
